/**
 * CDN Entry Point for Fluid Web Components
 *
 * This file serves as the entry point for the CDN-hosted version of Fluid web components.
 * It provides an improved developer experience with:
 * 1. Shadow DOM style encapsulation
 * 2. Multiple initialization options including data attributes
 * 3. Global access to Fluid functions
 */

import type { AttributionData, FairshareConfig } from "@fluid-app/fairshare";
import { initializeFluid as originalInitializeFluid } from "@fluid-app/fluid";
// Step 3: Export all SDK functions directly
import * as fluidSdkFunctions from "@fluid-app/fluid";
import { closeCart, openCart, toggleCart } from "@fluid-app/react-widgets";

import { registerWebComponents } from "./index";

// Define cart loading event names
export const CART_LOADING_EVENTS = {
  CART_LOADING_START: "CART_LOADING_START",
  CART_LOADING_END: "CART_LOADING_END",
};

// Custom type that extends FairshareConfig with the apiEndpoint property
type FluidConfig = FairshareConfig & {
  apiEndpoint?: string;
};

// Script ID used to find our script element
const FLUID_SCRIPT_ID = "fluid-cdn-script";

// Define a custom window interface with Fluid SDK properties
interface FluidWindow extends Window {
  FLUID_CONFIG?: FluidConfig;
  FLUID_SDK_INITIALIZED?: boolean;
  FLUID_SDK_INITIALIZING?: boolean;
  __FLUID_SDK_READY?: () => void;
  __FLUID_SDK_READY_PROMISE?: Promise<void>;
  __FLUID_CURRENT_SCRIPT?: HTMLScriptElement;
  __FLUID_STYLE_URL?: string;
  __FLUID_STYLE_TEXT?: string;
  FluidCommerceSDK?: {
    initializeFluid: (config: FluidConfig) => void;
    [key: string]: unknown; // Allow for dynamic properties
  };
  fluidCart?: {
    open: () => void;
    close: () => void;
    toggle: () => void;
  };
  __FLUID_SHADOW_STYLESHEET?: CSSStyleSheet;
  __FLUID_SHADOW_STYLESHEET_LOADING?: boolean;
}

// Type-safe access to the window object
const fluidWindow = window as FluidWindow;

// Immediately capture current script during initial execution
// This is critical since document.currentScript is only available during initial script execution
const INITIAL_SCRIPT = document.currentScript as HTMLScriptElement | null;
if (INITIAL_SCRIPT) {
  // Store it globally for later access
  fluidWindow.__FLUID_CURRENT_SCRIPT = INITIAL_SCRIPT;

  // Add the ID to make it easier to find later
  if (!INITIAL_SCRIPT.id) {
    INITIAL_SCRIPT.id = FLUID_SCRIPT_ID;
  }

  console.log(
    "Successfully captured current script during execution:",
    INITIAL_SCRIPT.src,
  );
}

/**
 * Get script element using multiple redundant strategies to ensure reliability
 */
function getScriptElement(): HTMLScriptElement | null {
  // Strategy 1: Use our globally stored reference (most reliable)
  if (fluidWindow.__FLUID_CURRENT_SCRIPT) {
    console.log(
      "Using globally stored script reference:",
      fluidWindow.__FLUID_CURRENT_SCRIPT.src,
    );
    return fluidWindow.__FLUID_CURRENT_SCRIPT;
  }

  // Strategy 2: Check for script with our ID
  const idScript = document.getElementById(
    FLUID_SCRIPT_ID,
  ) as HTMLScriptElement | null;
  if (idScript) {
    console.log("Found script via ID:", idScript.src);
    fluidWindow.__FLUID_CURRENT_SCRIPT = idScript; // Store for future use
    return idScript;
  }

  // Strategy 3: Try document.currentScript (might not be available outside initial execution)
  const currentScript = document.currentScript as HTMLScriptElement | null;
  if (currentScript) {
    console.log("Found script via document.currentScript:", currentScript.src);
    currentScript.id = FLUID_SCRIPT_ID;
    fluidWindow.__FLUID_CURRENT_SCRIPT = currentScript; // Store for future use
    return currentScript;
  }

  // Strategy 4: Find by URL patterns and attributes
  const allScripts = Array.from(document.getElementsByTagName("script"));
  console.log(`Searching through ${allScripts.length} scripts on page...`);

  // First, try to find scripts with our attributes
  const scriptWithAttrs = allScripts.find(
    (script) =>
      script.hasAttribute("data-fluid-shop") ||
      script.hasAttribute("data-share-guid") ||
      script.id === FLUID_SCRIPT_ID,
  ) as HTMLScriptElement | null;

  if (scriptWithAttrs) {
    console.log("Found script with Fluid attributes:", scriptWithAttrs.src);
    scriptWithAttrs.id = FLUID_SCRIPT_ID;
    fluidWindow.__FLUID_CURRENT_SCRIPT = scriptWithAttrs; // Store for future use
    return scriptWithAttrs;
  }

  // Next, try to find by URL patterns
  const scriptByUrl = allScripts.find(
    (script) =>
      script.src &&
      (script.src.includes("cdn.js") ||
        script.src.includes("@fluid-app/web-widgets") ||
        script.src.includes("fluid-app") ||
        script.src.includes("fluid-cdn")),
  ) as HTMLScriptElement | null;

  if (scriptByUrl) {
    console.log("Found script via URL pattern:", scriptByUrl.src);
    scriptByUrl.id = FLUID_SCRIPT_ID;
    fluidWindow.__FLUID_CURRENT_SCRIPT = scriptByUrl; // Store for future use
    return scriptByUrl;
  }

  // Last resort: just get the last script on the page
  if (allScripts.length > 0) {
    const lastScript = allScripts[
      allScripts.length - 1
    ] as HTMLScriptElement | null;
    if (!lastScript) {
      console.warn("No scripts found on page");
      return null;
    }
    console.log("Using last script on page as fallback:", lastScript.src);
    lastScript.id = FLUID_SCRIPT_ID;
    fluidWindow.__FLUID_CURRENT_SCRIPT = lastScript; // Store for future use
    return lastScript;
  }

  console.warn("All script detection methods failed");
  return null;
}

// Attempt to get the script element
const scriptElement = getScriptElement();
if (scriptElement) {
  console.log("Fluid script element found:", scriptElement.src);

  // Log all attributes for debugging
  const attrs = Array.from(scriptElement.attributes);
  if (attrs.length > 0) {
    console.log("Script attributes:");
    for (const attr of attrs) {
      console.log(`  ${attr.name}="${attr.value}"`);
    }
  }
} else {
  console.warn(
    "Could not find Fluid script element. Configuration via data attributes will not work.",
  );
}

/**
 * Load and cache styles for Shadow DOM injection instead of adding to document.head
 */
function injectStyles(): void {
  if (!scriptElement) {
    console.warn("Could not find script element for style loading");
    return;
  }

  const scriptSrc = scriptElement.src;
  const basePath = scriptSrc.substring(0, scriptSrc.lastIndexOf("/"));
  const styleUrl = `${basePath}/styles.css`;

  // Store the style URL globally for use with Shadow DOM components
  fluidWindow.__FLUID_STYLE_URL = styleUrl;

  // Fetch and cache the stylesheet content for shadow DOM injection
  fetch(styleUrl)
    .then((response) => response.text())
    .then((cssText) => {
      // Store the CSS text globally for components to access
      fluidWindow.__FLUID_STYLE_TEXT = cssText;
      console.log("Cached Fluid styles for Shadow DOM components");
    })
    .catch((error) => {
      console.error(
        "Failed to fetch stylesheet for Shadow DOM injection:",
        error,
      );
    });
}

// Update the injectStylesIntoShadowRoot function to support Constructable StyleSheets
function injectStylesIntoShadowRoot(shadowRoot: ShadowRoot): void {
  // Check if we can use Constructable StyleSheets
  if (supportsConstructableStyleSheets()) {
    injectStylesModern(shadowRoot);
  } else {
    injectStylesLegacy(shadowRoot);
  }
}

// Check if browser supports Constructable StyleSheets
function supportsConstructableStyleSheets(): boolean {
  return "adoptedStyleSheets" in ShadowRoot.prototype;
}

// Modern approach using Constructable StyleSheets API
function injectStylesModern(shadowRoot: ShadowRoot): void {
  // If we already have our global cached stylesheet, use it
  if (fluidWindow.__FLUID_SHADOW_STYLESHEET) {
    // Add stylesheet to shadow root if not already there
    const stylesheet = fluidWindow.__FLUID_SHADOW_STYLESHEET;
    shadowRoot.adoptedStyleSheets = [
      ...Array.from(shadowRoot.adoptedStyleSheets),
      stylesheet,
    ];
    return;
  }

  // If we don't have a stylesheet yet, create one
  if (!fluidWindow.__FLUID_SHADOW_STYLESHEET_LOADING) {
    fluidWindow.__FLUID_SHADOW_STYLESHEET_LOADING = true;

    try {
      // Create new stylesheet
      const stylesheet = new CSSStyleSheet();

      // If we have CSS text, use it directly
      const cssText = fluidWindow.__FLUID_STYLE_TEXT;
      if (cssText) {
        // Replace the stylesheet contents
        stylesheet.replaceSync(cssText);

        // Cache the stylesheet for future use
        fluidWindow.__FLUID_SHADOW_STYLESHEET = stylesheet;

        // Apply to this shadow root
        shadowRoot.adoptedStyleSheets = [
          ...Array.from(shadowRoot.adoptedStyleSheets),
          stylesheet,
        ];
      } else if (fluidWindow.__FLUID_STYLE_URL) {
        // If we have a URL but no CSS text, we'll need to fetch it
        fetch(fluidWindow.__FLUID_STYLE_URL)
          .then((response) => response.text())
          .then((text) => {
            // Cache the CSS text
            fluidWindow.__FLUID_STYLE_TEXT = text;

            // Create and apply stylesheet
            const sheet = new CSSStyleSheet();
            sheet.replaceSync(text);

            // Cache the stylesheet
            fluidWindow.__FLUID_SHADOW_STYLESHEET = sheet;

            // Apply to this shadow root
            shadowRoot.adoptedStyleSheets = [
              ...Array.from(shadowRoot.adoptedStyleSheets),
              sheet,
            ];
          })
          .catch((error) => {
            console.error("Failed to load styles:", error);
            // Fall back to legacy approach
            injectStylesLegacy(shadowRoot);
          })
          .finally(() => {
            fluidWindow.__FLUID_SHADOW_STYLESHEET_LOADING = false;
          });
      }
    } catch (error) {
      console.error("Error creating stylesheet:", error);
      // Fall back to legacy approach
      injectStylesLegacy(shadowRoot);
      fluidWindow.__FLUID_SHADOW_STYLESHEET_LOADING = false;
    }
  }
}

// Legacy style injection approach (fallback)
function injectStylesLegacy(shadowRoot: ShadowRoot): void {
  // Check if the cached CSS text is available
  const cssText = fluidWindow.__FLUID_STYLE_TEXT;

  if (cssText) {
    // Create a style element
    const styleElement = document.createElement("style");
    styleElement.textContent = cssText;
    styleElement.setAttribute("data-fluid-injected", "true");
    shadowRoot.appendChild(styleElement);
    console.log("Injected styles into shadow root");
  } else {
    // Fallback to creating a link if text isn't available
    const styleUrl = fluidWindow.__FLUID_STYLE_URL;
    if (styleUrl) {
      const linkElement = document.createElement("link");
      linkElement.rel = "stylesheet";
      linkElement.href = styleUrl;
      shadowRoot.appendChild(linkElement);
      console.log("Injected stylesheet link into shadow root");
    }
  }
}

// Create a mutation observer to watch for new fluid components with shadow roots
function setupShadowDomStyleObserver(): void {
  // Look for all elements that might be fluid components with shadow DOM
  const observeNewFluidComponents = () => {
    // Find all elements with the attribute data-fluid-widget
    const fluidElements = document.querySelectorAll(
      '[data-fluid-widget="true"]',
    );

    fluidElements.forEach((element) => {
      if (element instanceof HTMLElement && element.shadowRoot) {
        // Check if styles are already applied
        const hasInjectedStyles = element.shadowRoot.querySelector(
          'style[data-fluid-injected="true"]',
        );
        const hasAdoptedStyles =
          supportsConstructableStyleSheets() &&
          fluidWindow.__FLUID_SHADOW_STYLESHEET &&
          Array.from(element.shadowRoot.adoptedStyleSheets).includes(
            fluidWindow.__FLUID_SHADOW_STYLESHEET,
          );

        // Only inject if no styles are present
        if (!hasInjectedStyles && !hasAdoptedStyles) {
          injectStylesIntoShadowRoot(element.shadowRoot);
          console.log("Injected stylesheet into shadow root");
        }

        // Find and ensure trigger elements also have styles applied
        ensureTriggerElementsHaveStyles(element);

        // Check for company-color attribute or style directly on the element
        const companyColorStyle =
          element.style.getPropertyValue("--company-color");
        const companyColorAttr =
          element.getAttribute("company-color") ??
          element.getAttribute("data-company-color");

        if (companyColorStyle || companyColorAttr) {
          // Use the style property first, then fallback to attribute
          const colorValue = companyColorStyle || companyColorAttr;

          // Apply the company color directly to the shadow root element
          const styleEl = document.createElement("style");
          styleEl.textContent = `
            :host {
              /* Set the company color */
              --company-color: ${colorValue};
            }
          `;
          styleEl.setAttribute("data-company-color-style", "true");

          // Add or replace the company color style
          const existingStyle = element.shadowRoot.querySelector(
            '[data-company-color-style="true"]',
          );
          if (existingStyle) {
            existingStyle.replaceWith(styleEl);
          } else {
            element.shadowRoot.appendChild(styleEl);
          }

          console.log(
            `Applied company color variable: ${colorValue} to shadow root`,
          );
        }
      }
    });
  };

  // Run immediately and then set up observer
  observeNewFluidComponents();

  // Set up a mutation observer to detect new components
  const observer = new MutationObserver((mutations) => {
    try {
      for (const mutation of mutations) {
        if (mutation.type === "childList" && mutation.addedNodes.length) {
          observeNewFluidComponents();
        }
      }
    } catch (error) {
      console.warn("Error processing MutationObserver mutations:", error);
    }
  });

  // Start observing with error handling
  try {
    // Explicitly check if document.body exists before observing
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!document.body) {
      console.warn(
        "document.body is not available yet, cannot set up MutationObserver",
      );
      console.log("Falling back to initial component scan only");
      return;
    }

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
    console.log("Set up observer for Fluid components with Shadow DOM");
  } catch (error) {
    console.warn("Failed to set up MutationObserver:", error);
    // Still run the initial scan even if observer setup fails
    console.log("Falling back to initial component scan only");
  }
}

// Modify the ensureTriggerElementsHaveStyles function to only propagate variables
function ensureTriggerElementsHaveStyles(containerElement: HTMLElement): void {
  // Get all trigger elements (inside and outside the shadow root)
  let triggerElements: HTMLElement[] = [];

  // Check in light DOM first (elements might be outside shadow DOM due to portal/cloning)
  const lightDomTriggers = Array.from(
    document.querySelectorAll('[data-fluid-trigger="true"]'),
  );
  if (lightDomTriggers.length > 0) {
    triggerElements = triggerElements.concat(lightDomTriggers as HTMLElement[]);
  }

  // Check in shadow DOM if it exists
  if (containerElement.shadowRoot) {
    const shadowDomTriggers = Array.from(
      containerElement.shadowRoot.querySelectorAll(
        '[data-fluid-trigger="true"]',
      ),
    );

    if (shadowDomTriggers.length > 0) {
      triggerElements = triggerElements.concat(
        shadowDomTriggers as HTMLElement[],
      );
    }
  }

  // Apply fluid class to each trigger element
  triggerElements.forEach((triggerEl) => {
    // Make sure it has the 'fluid' class
    if (!triggerEl.classList.contains("fluid")) {
      triggerEl.classList.add("fluid");
    }

    // Add data attribute to mark as fluid trigger if not already present
    if (!triggerEl.hasAttribute("data-fluid-styles-applied")) {
      triggerEl.setAttribute("data-fluid-styles-applied", "true");
    }
  });
}

/**
 * Parse data attributes from script element for configuration
 * Supports attribution override via data attributes for all attribution methods:
 * - data-share-guid: Share GUID attribution
 * - data-fluid-rep-id: Fluid Rep ID attribution (numeric)
 * - data-email: Email attribution
 * - data-username: Username attribution
 * - data-external-id: External ID attribution
 *
 * Attribution methods are checked in priority order and the first found is used.
 */
function parseDataAttributes(): FluidConfig | null {
  if (!scriptElement) {
    console.warn("Could not find script element for data attribute parsing");
    return null;
  }

  // Debug: Print all attributes
  console.log(
    "Script attributes:",
    Array.from(scriptElement.attributes)
      .map((attr) => `${attr.name}=${attr.value}`)
      .join(", "),
  );

  const config: FluidConfig = {
    fluidShop: "",
  };

  // Get fluidShop from data-fluid-shop
  const fluidShop = scriptElement.getAttribute("data-fluid-shop");
  if (fluidShop) {
    config.fluidShop = fluidShop;
    console.log("Found shop ID from data attribute:", fluidShop);
  }

  // Parse attribution override from data attributes
  let attributionOverride: AttributionData | undefined;

  // Check for each supported attribution method (prioritized in order)
  // 1. Share GUID attribution
  const shareGuid = scriptElement.getAttribute("data-share-guid");
  if (shareGuid) {
    attributionOverride = { share_guid: shareGuid };
    console.log(
      "Found attribution override share GUID from data attribute:",
      shareGuid,
    );
  }

  // 2. Fluid Rep ID attribution (only if no share GUID)
  if (!attributionOverride) {
    const fluidRepId = scriptElement.getAttribute("data-fluid-rep-id");
    if (fluidRepId) {
      const repId = parseInt(fluidRepId, 10);
      if (!isNaN(repId)) {
        attributionOverride = { fluid_rep_id: repId };
        console.log(
          "Found attribution override fluid rep ID from data attribute:",
          repId,
        );
      } else {
        console.warn("Invalid fluid rep ID in data-fluid-rep-id:", fluidRepId);
      }
    }
  }

  // 3. Email attribution (only if no previous attribution found)
  if (!attributionOverride) {
    const email = scriptElement.getAttribute("data-email");
    if (email) {
      // Basic email validation
      if (email.includes("@") && email.includes(".")) {
        attributionOverride = { email };
        console.log(
          "Found attribution override email from data attribute:",
          email,
        );
      } else {
        console.warn("Invalid email format in data-email:", email);
      }
    }
  }

  // 4. Username attribution (only if no previous attribution found)
  if (!attributionOverride) {
    const username = scriptElement.getAttribute("data-username");
    if (username) {
      attributionOverride = { username };
      console.log(
        "Found attribution override username from data attribute:",
        username,
      );
    }
  }

  // 5. External ID attribution (only if no previous attribution found)
  if (!attributionOverride) {
    const externalId = scriptElement.getAttribute("data-external-id");
    if (externalId) {
      attributionOverride = { external_id: externalId };
      console.log(
        "Found attribution override external ID from data attribute:",
        externalId,
      );
    }
  }

  // Add attribution override to config if any was found
  if (attributionOverride) {
    config.attributionOverride = attributionOverride;
    console.log("Attribution override configured:", attributionOverride);
  }

  // Check for deprecated attribution pattern attributes and show warnings
  const deprecatedAttrs = [
    "data-path-attribution-pattern",
    "data-subdomain-attribution-pattern",
    "data-enable-path-attribution",
    "data-enable-subdomain-attribution",
  ];

  for (const attrName of deprecatedAttrs) {
    if (scriptElement.hasAttribute(attrName)) {
      console.warn(
        `⚠️  REMOVED: "${attrName}" attribute has been removed and is no longer supported. ` +
          `Attribution is now calculated server-side. Use "data-share-guid" for attribution override instead. ` +
          `See migration guide: https://docs.fluid.app/migration/server-side-attribution`,
      );
    }
  }

  // Get debug mode from data-debug
  const debug = scriptElement.getAttribute("data-debug");
  if (debug !== null) {
    config.debug = debug === "true";
    console.log("Debug mode set to:", config.debug);
  }

  // Get custom API endpoint from data-fluid-api-base-url
  const apiBaseUrl = scriptElement.getAttribute("data-fluid-api-base-url");
  if (apiBaseUrl) {
    config.apiEndpoint = apiBaseUrl;
    console.log("Found custom API endpoint from data attribute:", apiBaseUrl);
  }

  // Only return if we have the minimum required config (fluidShop)
  if (config.fluidShop) {
    console.log(
      "Valid configuration from data attributes:",
      JSON.stringify(config),
    );
    return config;
  }

  console.warn(
    "Invalid or incomplete configuration from data attributes - missing fluidShop",
  );
  return null;
}

// Create synchronized wrapper function for SDK initialization
function syncInitializeFluid(config: FluidConfig): void {
  if (fluidWindow.FLUID_SDK_INITIALIZING || fluidWindow.FLUID_SDK_INITIALIZED) {
    console.log("SDK already initializing or initialized, skipping");
    return;
  }

  try {
    console.log("Initializing Fluid SDK with config:", JSON.stringify(config));

    // Log specific attribution configuration for debugging
    if (config.attributionOverride) {
      console.log(
        "🎯 CDN: Attribution override will be set:",
        config.attributionOverride,
      );
    } else {
      console.log(
        "🌐 CDN: Using server-side attribution (no attribution override)",
      );
    }

    // Set initializing flag
    fluidWindow.FLUID_SDK_INITIALIZING = true;

    // Call the original initialization function
    originalInitializeFluid(config);

    // Mark as initialized for components to detect
    fluidWindow.FLUID_SDK_INITIALIZED = true;
    fluidWindow.FLUID_SDK_INITIALIZING = false;

    // Log attribution configuration status
    if (config.attributionOverride) {
      // Determine attribution type for logging
      let attributionType = "unknown";
      if ("share_guid" in config.attributionOverride) {
        attributionType = "Share GUID";
      } else if ("fluid_rep_id" in config.attributionOverride) {
        attributionType = "Fluid Rep ID";
      } else if ("email" in config.attributionOverride) {
        attributionType = "Email";
      } else if ("username" in config.attributionOverride) {
        attributionType = "Username";
      } else if ("external_id" in config.attributionOverride) {
        attributionType = "External ID";
      }

      console.log(
        `✅ Attribution override ${attributionType} initialized:`,
        config.attributionOverride,
      );
    } else {
      console.log(
        "ℹ️  Server-side attribution enabled (no attribution override)",
      );
    }
    console.log("ℹ️  Page change detection active for attribution refresh");

    // Trigger ready callback if it exists
    if (typeof fluidWindow.__FLUID_SDK_READY === "function") {
      fluidWindow.__FLUID_SDK_READY();
    }

    console.log(
      "Fluid SDK initialized successfully with shop:",
      config.fluidShop,
    );
  } catch (error) {
    fluidWindow.FLUID_SDK_INITIALIZING = false;
    console.error("Failed to initialize Fluid SDK:", error);
  }
}

/**
 * Setup a promise to resolve when SDK is ready
 */
const sdkReadyPromise = new Promise<void>((resolve) => {
  if (fluidWindow.FLUID_SDK_INITIALIZED) {
    console.log("SDK already initialized, resolving immediately");
    resolve();
  } else {
    console.log("Setting up SDK ready callback...");
    fluidWindow.__FLUID_SDK_READY = () => {
      console.log("Fluid SDK initialization detected via callback");
      resolve();
    };

    const checkInterval = setInterval(() => {
      if (fluidWindow.FLUID_SDK_INITIALIZED) {
        clearInterval(checkInterval);
        console.log("Fluid SDK initialization detected by interval check");
        resolve();
      }
    }, 100);

    // Set a timeout to prevent waiting forever
    setTimeout(() => {
      clearInterval(checkInterval);
      console.warn(
        "Timed out waiting for Fluid SDK initialization, continuing anyway",
      );
      // If timed out but we can see the SDK is initializing, give it just a bit more time
      if (fluidWindow.FLUID_SDK_INITIALIZING) {
        console.log("SDK is still initializing, giving it a bit more time...");
        setTimeout(() => {
          resolve();
        }, 1000);
      } else {
        resolve();
      }
    }, 5000); // Increased timeout to 5 seconds
  }
}).catch((err) => {
  console.error("Error in SDK Ready Promise setup:", err);
});

// Create a flag to track registration status and prevent double registration
let componentsRegistered = false;

/**
 * Register web components only once
 * IMPORTANT: Components must be registered AFTER SDK is initialized
 */
async function registerComponentsOnce(): Promise<void> {
  if (componentsRegistered) {
    console.log("Web components already registered, skipping");
    return;
  }

  try {
    // Important: Make sure SDK is initialized BEFORE registering components
    // Wait for SDK ready promise to resolve
    console.log("Waiting for SDK to be ready before registering components...");
    await sdkReadyPromise;
    console.log("SDK ready, now registering components...");

    // Set flag BEFORE registering to prevent race conditions
    componentsRegistered = true;
    await registerWebComponents();
    console.log("Web components registered successfully");
  } catch (err) {
    // If it's a duplicate registration error, just log it (we already set the flag)
    const errorMessage = String(err);
    if (errorMessage.includes("already been used with this registry")) {
      console.log(
        "Components already registered elsewhere (this is normal in some cases)",
      );
    } else {
      console.error("Error registering web components:", err);
      // Don't unset the flag - prevents infinite retry loops
    }
  }
}

/**
 * Primary initialization function - checks for config sources and initializes
 */
function initializeFromConfig(): boolean {
  try {
    // Try to initialize from data attributes first (immediate execution)
    const configFromAttributes = parseDataAttributes();
    if (configFromAttributes) {
      console.log("Initializing from data attributes");
      syncInitializeFluid(configFromAttributes);
      return true;
    }

    // Then check for global config object
    if (fluidWindow.FLUID_CONFIG) {
      console.log("Initializing from global FLUID_CONFIG");
      syncInitializeFluid(fluidWindow.FLUID_CONFIG);
      return true;
    }

    console.log(
      "No configuration found during initialization. Please initialize manually or add data attributes.",
    );
    return false;
  } catch (error) {
    console.error("Error during initialization:", error);
    return false;
  }
}

// Initialization sequence:
// 1. Inject styles immediately (don't wait for full initialization)
// 2. Initialize SDK
// 3. Export all SDK functions directly
// 4. Register components
// 5. Setup listeners

// Step 1: Immediately inject styles
injectStyles();

// Step 2: Initialize SDK
const initialized = initializeFromConfig();

// Create a clean SDK export with all functions
const fluidExports = {
  ...fluidSdkFunctions,
  // Add cart controls for convenience
  cart: {
    open: openCart,
    close: closeCart,
    toggle: toggleCart,
  },
  // Add loading events for external use
  CART_LOADING_EVENTS,
  // Add the initialization function
  initializeFluid: syncInitializeFluid,
};

// Export as global FluidCommerceSDK
// @ts-expect-error - This is intentional for IIFE build output
window.FluidCommerceSDK = fluidExports;

// Also re-export each function individually to ensure it's accessible
// This helps prevent tree-shaking from removing functions during bundling
void (async () => {
  try {
    // Import the module dynamically to get all exports
    const fluidModule = await import("@fluid-app/fluid");

    // Copy each function to the FluidCommerceSDK global
    Object.keys(fluidModule).forEach((key) => {
      // @ts-expect-error - This is intentional
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      window.FluidCommerceSDK[key] = fluidModule[key];
    });
  } catch (error) {
    console.error("Error setting up global SDK functions:", error);
  }
})();

// Store the SDK ready promise for components to use
fluidWindow.__FLUID_SDK_READY_PROMISE = sdkReadyPromise;

// Step 4: Register components
void registerComponentsOnce().then(() => {
  // Setup function for DOM-dependent operations
  const setupDomDependentFeatures = async () => {
    // Set up observer for shadow DOM styling to ensure style encapsulation
    setupShadowDomStyleObserver();

    // Register props globally as a fallback mechanism
    await registerPropsGlobally();

    // For backward compatibility with current data-fluid-cart attributes,
    // still assign to fluidCart (the main SDK already has these in cart)
    fluidWindow.fluidCart = fluidExports.cart;

    // Add click listener for data-fluid-cart attribute
    document.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;

      // Check for cart controls
      const cartAction = target.getAttribute("data-fluid-cart");
      if (cartAction && fluidWindow.fluidCart) {
        if (cartAction === "open") fluidWindow.fluidCart.open();
        else if (cartAction === "close") fluidWindow.fluidCart.close();
        else if (cartAction === "toggle") fluidWindow.fluidCart.toggle();
      }

      // Check for combined add to cart (enrollment pack + items)
      const combinedAddData = target.getAttribute("data-fluid-add-combined");
      if (combinedAddData) {
        void (async () => {
          try {
            const parts = combinedAddData.split("+");
            if (parts.length !== 2 || !parts[0] || !parts[1]) {
              console.error(
                "Invalid format for data-fluid-add-combined. Expected: 'enrollmentPackId+variantId1,variantId2'",
              );
              return;
            }

            const enrollmentPackId = parts[0].trim();
            const variantIdsString = parts[1].trim();

            const packId = parseInt(enrollmentPackId, 10);
            const variantIds = variantIdsString
              .split(",")
              .map((id) => parseInt(id.trim(), 10));

            // Check if IDs are valid
            if (isNaN(packId) || variantIds.some(isNaN)) {
              console.error(
                "Invalid format for data-fluid-add-combined. Expected: 'enrollmentPackId+variantId1,variantId2'",
              );
              return;
            }

            // Only proceed if SDK is available
            if (!fluidWindow.FLUID_SDK_INITIALIZED) {
              console.warn(
                "Fluid SDK not initialized, can't add items to cart",
              );
              return;
            }

            // Check for quantity attribute
            const quantityAttr = target.getAttribute("data-fluid-quantity");
            const quantity = quantityAttr ? parseInt(quantityAttr, 10) : 1;

            // Check for subscription attribute
            const subscribeAttr = target.getAttribute("data-fluid-subscribe");
            let subscribeValues: boolean[] | undefined;

            if (subscribeAttr) {
              // Handle comma-separated subscribe values for multiple items
              if (subscribeAttr.includes(",")) {
                subscribeValues = subscribeAttr
                  .split(",")
                  .map((val) => val.trim().toLowerCase() === "true");
              } else {
                // Single value for all items
                const subscribeValue = subscribeAttr.toLowerCase() === "true";
                subscribeValues = Array(variantIds.length).fill(subscribeValue);
              }
            }

            // Import the functions dynamically
            const { addCartItems, addEnrollmentPack } = await import(
              "@fluid-app/fluid"
            );

            // Emit loading start event for combined operation
            window.dispatchEvent(
              new CustomEvent(CART_LOADING_EVENTS.CART_LOADING_START, {
                detail: { packId, variantIds, type: "combined" },
              }),
            );

            try {
              // First add the enrollment pack
              await addEnrollmentPack(packId);

              // Then add the items
              const items = variantIds.map((variantId, index) => ({
                variant_id: variantId,
                quantity,
                subscribe: subscribeValues?.[index],
              }));

              await addCartItems(items);

              // Check if we should open the cart after adding
              const openCartAfterAdd = target.getAttribute(
                "data-fluid-open-cart-after-add",
              );
              if (openCartAfterAdd !== "false" && fluidWindow.fluidCart) {
                fluidWindow.fluidCart.open();
              }
            } catch (error) {
              console.error("Failed to add combined items to cart:", error);
            } finally {
              // Emit loading end event
              window.dispatchEvent(
                new CustomEvent(CART_LOADING_EVENTS.CART_LOADING_END, {
                  detail: { packId, variantIds, type: "combined" },
                }),
              );
            }
          } catch (error) {
            console.error("Error processing combined add to cart:", error);
            // Always emit loading end event in case of error
            window.dispatchEvent(
              new CustomEvent(CART_LOADING_EVENTS.CART_LOADING_END, {
                detail: { error: true },
              }),
            );
          }
        })();
      }

      // Check for both enrollment pack and regular items on the same element (combined add)
      const enrollmentPackId = target.getAttribute(
        "data-fluid-add-enrollment-pack",
      );
      const addToCartVariantId = target.getAttribute("data-fluid-add-to-cart");
      if (enrollmentPackId && addToCartVariantId) {
        void (async () => {
          try {
            const packId = parseInt(enrollmentPackId, 10);
            const variantIds = addToCartVariantId
              .split(",")
              .map((id) => parseInt(id.trim(), 10));

            // Check if IDs are valid
            if (isNaN(packId) || variantIds.some(isNaN)) {
              console.error("Invalid enrollment pack ID or variant IDs");
              return;
            }

            // Only proceed if SDK is available
            if (!fluidWindow.FLUID_SDK_INITIALIZED) {
              console.warn(
                "Fluid SDK not initialized, can't add items to cart",
              );
              return;
            }

            // Check for quantity attribute
            const quantityAttr = target.getAttribute("data-fluid-quantity");
            const quantity = quantityAttr ? parseInt(quantityAttr, 10) : 1;

            // Check for subscription attribute
            const subscribeAttr = target.getAttribute("data-fluid-subscribe");
            let subscribeValues: boolean[] | undefined;

            if (subscribeAttr) {
              // Handle comma-separated subscribe values for multiple items
              if (subscribeAttr.includes(",")) {
                subscribeValues = subscribeAttr
                  .split(",")
                  .map((val) => val.trim().toLowerCase() === "true");
              } else {
                // Single value for all items
                const subscribeValue = subscribeAttr.toLowerCase() === "true";
                subscribeValues = Array(variantIds.length).fill(subscribeValue);
              }
            }

            // Import the functions dynamically
            const { addCartItems, addEnrollmentPack } = await import(
              "@fluid-app/fluid"
            );

            // Emit loading start event for combined operation
            window.dispatchEvent(
              new CustomEvent(CART_LOADING_EVENTS.CART_LOADING_START, {
                detail: { packId, variantIds, type: "combined" },
              }),
            );

            try {
              // First add the enrollment pack
              await addEnrollmentPack(packId);

              // Then add the items
              const items = variantIds.map((variantId, index) => ({
                variant_id: variantId,
                quantity,
                subscribe: subscribeValues?.[index],
              }));

              await addCartItems(items);

              // Check if we should open the cart after adding
              const openCartAfterAdd = target.getAttribute(
                "data-fluid-open-cart-after-add",
              );
              if (openCartAfterAdd !== "false" && fluidWindow.fluidCart) {
                fluidWindow.fluidCart.open();
              }
            } catch (error) {
              console.error("Failed to add combined items to cart:", error);
            } finally {
              // Emit loading end event
              window.dispatchEvent(
                new CustomEvent(CART_LOADING_EVENTS.CART_LOADING_END, {
                  detail: { packId, variantIds, type: "combined" },
                }),
              );
            }
          } catch (error) {
            console.error("Error processing combined add to cart:", error);
            // Always emit loading end event in case of error
            window.dispatchEvent(
              new CustomEvent(CART_LOADING_EVENTS.CART_LOADING_END, {
                detail: { error: true },
              }),
            );
          }
        })();

        // We already handled both attributes together, so return to prevent individual handling
        return;
      }

      // Check for add to cart
      if (addToCartVariantId) {
        void (async () => {
          try {
            // Handle comma-separated list of variant IDs
            const variantIds = addToCartVariantId
              .split(",")
              .map((id) => parseInt(id.trim(), 10));

            // Only proceed if at least one valid variant ID
            if (variantIds.length > 0 && !variantIds.some(isNaN)) {
              // Check for quantity attribute
              const quantityAttr = target.getAttribute("data-fluid-quantity");
              const quantity = quantityAttr ? parseInt(quantityAttr, 10) : 1;

              // Check for subscription attribute
              const subscribeAttr = target.getAttribute("data-fluid-subscribe");
              let subscribeValues: boolean[] | undefined;

              if (subscribeAttr) {
                // Handle comma-separated subscribe values for multiple items
                if (subscribeAttr.includes(",")) {
                  subscribeValues = subscribeAttr
                    .split(",")
                    .map((val) => val.trim().toLowerCase() === "true");
                } else {
                  // Single value for all items
                  const subscribeValue = subscribeAttr.toLowerCase() === "true";
                  subscribeValues = Array(variantIds.length).fill(
                    subscribeValue,
                  );
                }
              }

              // Only proceed if SDK is available
              if (fluidWindow.FLUID_SDK_INITIALIZED) {
                // Import the function dynamically to avoid circular dependencies
                const { addCartItems } = await import("@fluid-app/fluid");

                if (variantIds.length === 1) {
                  // Single item case
                  const variantId = variantIds[0];
                  if (variantId !== undefined) {
                    // Open cart after adding item by default, unless explicitly disabled
                    const openCartAfterAdd = target.getAttribute(
                      "data-fluid-open-cart-after-add",
                    );
                    // Only check for explicit "false" value to prevent auto-opening
                    if (openCartAfterAdd !== "false" && fluidWindow.fluidCart) {
                      fluidWindow.fluidCart.open();
                    }

                    // Emit loading start event
                    window.dispatchEvent(
                      new CustomEvent(CART_LOADING_EVENTS.CART_LOADING_START, {
                        detail: { variantId, type: "item" },
                      }),
                    );

                    // Add item to cart
                    addCartItems(variantId, {
                      quantity,
                      subscribe: subscribeValues?.[0],
                    })
                      .catch((error) => {
                        console.error("Failed to add item to cart:", error);
                      })
                      .finally(() => {
                        // Emit loading end event
                        window.dispatchEvent(
                          new CustomEvent(
                            CART_LOADING_EVENTS.CART_LOADING_END,
                            {
                              detail: { variantId, type: "item" },
                            },
                          ),
                        );
                      });
                  }
                } else {
                  // Multiple items case
                  const items = variantIds.map((variantId, index) => ({
                    variant_id: variantId,
                    quantity,
                    subscribe: subscribeValues?.[index],
                  }));

                  // Open cart after adding items by default, unless explicitly disabled
                  const openCartAfterAdd = target.getAttribute(
                    "data-fluid-open-cart-after-add",
                  );
                  // Only check for explicit "false" value to prevent auto-opening
                  if (openCartAfterAdd !== "false" && fluidWindow.fluidCart) {
                    fluidWindow.fluidCart.open();
                  }

                  // Emit loading start event for multiple items
                  window.dispatchEvent(
                    new CustomEvent(CART_LOADING_EVENTS.CART_LOADING_START, {
                      detail: { variantIds, type: "items" },
                    }),
                  );

                  addCartItems(items)
                    .catch((error) => {
                      console.error("Failed to add items to cart:", error);
                    })
                    .finally(() => {
                      // Emit loading end event for multiple items
                      window.dispatchEvent(
                        new CustomEvent(CART_LOADING_EVENTS.CART_LOADING_END, {
                          detail: { variantIds, type: "items" },
                        }),
                      );
                    });
                }
              } else {
                console.warn(
                  "Fluid SDK not initialized, can't add item to cart",
                );
              }
            }
          } catch (error) {
            console.error("Error adding item to cart:", error);
            // Always emit loading end event in case of error
            window.dispatchEvent(
              new CustomEvent(CART_LOADING_EVENTS.CART_LOADING_END, {
                detail: { error: true },
              }),
            );
          }
        })();
      }

      // Check for add enrollment pack (only when not already handled in combined case)
      if (enrollmentPackId && !addToCartVariantId) {
        void (async () => {
          try {
            const packId = parseInt(enrollmentPackId, 10);
            if (!isNaN(packId)) {
              // Only proceed if SDK is available
              if (fluidWindow.FLUID_SDK_INITIALIZED) {
                try {
                  // Import the function dynamically to avoid circular dependencies
                  const fluidWithEnrollment = await import("@fluid-app/fluid");

                  if (
                    typeof fluidWithEnrollment.addEnrollmentPack === "function"
                  ) {
                    // Open cart after adding enrollment pack by default, unless explicitly disabled
                    const openCartAfterAdd = target.getAttribute(
                      "data-fluid-open-cart-after-add",
                    );
                    // Only check for explicit "false" value to prevent auto-opening
                    if (openCartAfterAdd !== "false" && fluidWindow.fluidCart) {
                      fluidWindow.fluidCart.open();
                    }

                    // Emit loading start event for enrollment pack
                    window.dispatchEvent(
                      new CustomEvent(CART_LOADING_EVENTS.CART_LOADING_START, {
                        detail: { packId, type: "enrollment" },
                      }),
                    );

                    // Add enrollment pack to cart
                    await fluidWithEnrollment.addEnrollmentPack(packId);

                    // Emit loading end event for enrollment pack
                    window.dispatchEvent(
                      new CustomEvent(CART_LOADING_EVENTS.CART_LOADING_END, {
                        detail: { packId, type: "enrollment" },
                      }),
                    );
                  } else {
                    console.warn(
                      "addEnrollmentPack function not available in @fluid-app/fluid module",
                    );
                  }
                } catch (importError) {
                  const errorMessage =
                    importError instanceof Error
                      ? importError.message
                      : String(importError);
                  console.error("Error importing fluid module:", errorMessage);
                  // Emit loading end event in case of error
                  window.dispatchEvent(
                    new CustomEvent(CART_LOADING_EVENTS.CART_LOADING_END, {
                      detail: { error: true },
                    }),
                  );
                }
              } else {
                console.warn(
                  "Fluid SDK not initialized, can't add enrollment pack to cart",
                );
              }
            }
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : String(error);
            console.error(
              "Error adding enrollment pack to cart:",
              errorMessage,
            );
            // Always emit loading end event in case of error
            window.dispatchEvent(
              new CustomEvent(CART_LOADING_EVENTS.CART_LOADING_END, {
                detail: { error: true },
              }),
            );
          }
        })();
      }
    });
  };

  // Check if DOM is already ready
  if (document.readyState === "loading") {
    // DOM is still loading, wait for DOMContentLoaded
    document.addEventListener("DOMContentLoaded", () => {
      void setupDomDependentFeatures();
    });
  } else {
    // DOM is already ready, run immediately
    void setupDomDependentFeatures();
  }
});

// If we couldn't initialize initially, try again when DOM is loaded
if (!initialized) {
  document.addEventListener("DOMContentLoaded", () => {
    initializeFromConfig();
  });
}

// Add a function that registers props globally
async function registerPropsGlobally(): Promise<void> {
  try {
    console.log("Attempting to register props globally on window object");

    // Define type for widget props
    interface WidgetProps {
      propNames: string[];
      propInfo?: {
        name: string;
        type: string;
        optional: boolean;
      }[];
    }

    // Create a typed window reference
    const typedWindow = window as unknown as Record<string, WidgetProps>;

    // Try to import all prop files
    const widgetTypes = [
      {
        name: "CartWidget",
        path: "./generated/cart-widget-props.js",
      },
      {
        name: "BannerWidget",
        path: "./generated/banner-widget-props.js",
      },
      {
        name: "LeadCaptureWidget",
        path: "./generated/lead-capture-widget-props.js",
      },
    ];

    for (const widget of widgetTypes) {
      try {
        // Try to load the props
        const propsModule: unknown = await import(
          /* webpackIgnore: true */ /* @vite-ignore */ widget.path
        );

        // Type guard to check if propsModule is a valid object with default property
        const isValidPropsModule = (
          module: unknown,
        ): module is {
          default: string[];
          propInfo?: WidgetProps["propInfo"];
        } => {
          return (
            typeof module === "object" &&
            module !== null &&
            "default" in module &&
            Array.isArray((module as { default: unknown }).default)
          );
        };

        if (isValidPropsModule(propsModule)) {
          // Create the window property
          const globalPropName = `fluid${widget.name}Props`;

          // Register on window with type-safe access
          typedWindow[globalPropName] = {
            propNames: propsModule.default,
            propInfo: propsModule.propInfo,
          };

          console.log(
            `Successfully registered ${globalPropName} on window object with ${propsModule.default.length} props`,
          );
        }
      } catch (err) {
        console.log(`Could not load props for ${widget.name}: ${String(err)}`);
      }
    }
  } catch (error) {
    console.error("Error registering props globally:", error);
  }
}
